// Translates rendered English text in place, using a phrase dictionary.
// Only text nodes and a few attributes change — never the DOM structure — so React stays in control.
// Anything inside [translate="no"], [data-i18n-skip] or <svg> (logos, wordmarks, legal text) is left untouched.

export type PhrasePattern = {
  re: RegExp;
  to: (match: RegExpMatchArray, lookup: (text: string) => string) => string | null;
};

const ATTRIBUTES = ['placeholder', 'aria-label', 'title'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE']);
const SKIP_SELECTOR = '[translate="no"], [data-i18n-skip], svg';
const LIST_SEPARATORS = [' · ', ' — ', ' – ', ' + ', ' / ', ', '];
const NEUTRAL = /^[\d\s:.,/%+°€$£–—-]*$/;

export function normalizePhrase(text: string) {
  return text.replace(/\s+/g, ' ').trim();
}

export function createPhraseLookup(phrases: Map<string, string>, patterns: PhrasePattern[]) {
  const lookup = (text: string) => phrases.get(text) ?? text;

  return (raw: string): string | null => {
    const key = normalizePhrase(raw);
    if (!key || NEUTRAL.test(key)) return null;

    const direct = phrases.get(key);
    if (direct !== undefined) return direct;

    for (const pattern of patterns) {
      const match = key.match(pattern.re);
      if (match) {
        const out = pattern.to(match, lookup);
        if (out) return out;
      }
    }

    // Composed labels such as "The Alps · January" or "Private aviation + yacht transfer":
    // translated only when every part is known
    for (const sep of LIST_SEPARATORS) {
      if (!key.includes(sep)) continue;
      const parts = key.split(sep).map((part) => {
        const p = part.trim();
        if (NEUTRAL.test(p)) return p;
        return phrases.get(p) ?? null;
      });
      if (parts.every((p) => p !== null)) return parts.join(sep);
    }
    return null;
  };
}

export function startDomTranslation(translate: (text: string) => string | null): () => void {
  const ownText = new WeakMap<Text, string>();
  const ownAttr = new WeakMap<Element, Record<string, string>>();

  const skipped = (el: Element | null) => !el || SKIP_TAGS.has(el.tagName) || !!el.closest(SKIP_SELECTOR);

  const doText = (node: Text) => {
    const value = node.nodeValue ?? '';
    if (!value.trim() || ownText.get(node) === value || skipped(node.parentElement)) return;
    const result = translate(value);
    if (result === null || result === normalizePhrase(value)) return;
    const lead = value.match(/^\s*/)?.[0] ?? '';
    const trail = value.match(/\s*$/)?.[0] ?? '';
    const next = lead + result + trail;
    ownText.set(node, next);
    node.nodeValue = next;
  };

  const doAttrs = (el: Element) => {
    if (skipped(el)) return;
    for (const name of ATTRIBUTES) {
      const value = el.getAttribute(name);
      if (!value) continue;
      const mine = ownAttr.get(el) ?? {};
      if (mine[name] === value) continue;
      const result = translate(value);
      if (result === null || result === value) continue;
      mine[name] = result;
      ownAttr.set(el, mine);
      el.setAttribute(name, result);
    }
  };

  const walk = (root: Node) => {
    if (root.nodeType === Node.TEXT_NODE) {
      doText(root as Text);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    const el = root as Element;
    if (skipped(el)) return;
    doAttrs(el);
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    let n = walker.nextNode();
    while (n) {
      if (n.nodeType === Node.TEXT_NODE) doText(n as Text);
      else doAttrs(n as Element);
      n = walker.nextNode();
    }
  };

  walk(document.documentElement);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'characterData') doText(m.target as Text);
      else if (m.type === 'attributes') doAttrs(m.target as Element);
      else m.addedNodes.forEach(walk);
    }
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRIBUTES,
  });

  return () => observer.disconnect();
}
